import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Warning,
  Error as ErrorIcon,
  Edit,
  Add,
  Close as CloseIcon,
  CheckCircle,
  Cancel,
  Info,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface ViolationsTabProps {
  violations?: {
    total: number;
    pendingFines: number;
    totalPendingFines: number;
    disputedViolations: number;
    hasOverdueFines: boolean;
    list: any[];
  };
  providerId: string;
  onRefresh: () => void;
}

const ViolationsTab: React.FC<ViolationsTabProps> = ({ violations, providerId, onRefresh }) => {
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Record violation form
  const [recordForm, setRecordForm] = useState({
    serviceRequestId: '',
    violationDate: new Date().toISOString().split('T')[0],
    adminNotes: '',
    notes: '',
  });

  // Edit violation form
  const [editForm, setEditForm] = useState({
    status: '',
    adminNotes: '',
    adjustedFineAmount: 0,
    appealDecision: '',
    appealDecisionNotes: '',
  });

  const violationsList = violations?.list || [];
  const violationStats = violations || {
    total: 0,
    pendingFines: 0,
    totalPendingFines: 0,
    disputedViolations: 0,
    hasOverdueFines: false,
  };

  const formatDate = (dateString: string | Date) => {
    if (!dateString) return 'N/A';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      return date.toLocaleDateString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'warning': return 'info';
      case 'fine_pending': return 'warning';
      case 'fine_unpaid': return 'error';
      case 'fine_collected': return 'success';
      case 'disputed': return 'secondary';
      case 'waived': return 'success';
      case 'reduced': return 'info';
      case 'provider_removed': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'warning': return 'Warning';
      case 'fine_pending': return 'Fine Pending';
      case 'fine_unpaid': return 'Fine Unpaid';
      case 'fine_collected': return 'Fine Collected';
      case 'disputed': return 'Disputed';
      case 'waived': return 'Waived';
      case 'reduced': return 'Reduced';
      case 'provider_removed': return 'Provider Removed';
      default: return status;
    }
  };

  const handleRecordViolation = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.post('/api/no-show-violations/record', {
        serviceRequestId: recordForm.serviceRequestId,
        violationDate: recordForm.violationDate,
        adminNotes: recordForm.adminNotes,
        notes: recordForm.notes,
      });

      if (response.success || (response as any).data) {
        setSuccess('Violation recorded successfully');
        setRecordDialogOpen(false);
        setRecordForm({
          serviceRequestId: '',
          violationDate: new Date().toISOString().split('T')[0],
          adminNotes: '',
          notes: '',
        });
        setTimeout(() => {
          onRefresh();
        }, 500);
      } else {
        setError((response as any).error || 'Failed to record violation');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to record violation');
    } finally {
      setLoading(false);
    }
  };

  const handleEditViolation = (violation: any) => {
    setSelectedViolation(violation);
    setEditForm({
      status: violation.status,
      adminNotes: violation.adminNotes || '',
      adjustedFineAmount: violation.adjustedFineAmount || violation.fineAmount || 0,
      appealDecision: violation.appealDecision || '',
      appealDecisionNotes: violation.appealDecisionNotes || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateViolation = async () => {
    if (!selectedViolation) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData: any = {
        status: editForm.status,
        adminNotes: editForm.adminNotes,
      };

      // If status is waived or reduced, update adjustedFineAmount
      if (editForm.status === 'waived') {
        updateData.adjustedFineAmount = 0;
      } else if (editForm.status === 'reduced') {
        updateData.adjustedFineAmount = editForm.adjustedFineAmount;
      }

      // If handling an appeal
      if (editForm.appealDecision) {
        updateData.appealDecision = editForm.appealDecision;
        updateData.appealDecisionNotes = editForm.appealDecisionNotes;
      }

      const response = await apiService.put(
        `/api/no-show-violations/${selectedViolation._id}/status`,
        updateData
      );

      if (response.success || (response as any).data) {
        setSuccess('Violation updated successfully');
        setEditDialogOpen(false);
        setSelectedViolation(null);
        setTimeout(() => {
          onRefresh();
        }, 500);
      } else {
        setError((response as any).error || 'Failed to update violation');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update violation');
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveFineAmount = (violation: any) => {
    return violation.adjustedFineAmount !== undefined
      ? violation.adjustedFineAmount
      : violation.fineAmount || 0;
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, minWidth: 200, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Total Violations</Typography>
          <Typography variant="h4">{violationStats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, minWidth: 200, flex: 1, borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="body2" color="text.secondary">Pending Fines</Typography>
          <Typography variant="h4" color="warning.main">{violationStats.pendingFines}</Typography>
          <Typography variant="body2" color="text.secondary">
            ${violationStats.totalPendingFines.toFixed(2)} total
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, minWidth: 200, flex: 1, borderLeft: '4px solid', borderColor: 'secondary.main' }}>
          <Typography variant="body2" color="text.secondary">Disputed</Typography>
          <Typography variant="h4" color="secondary.main">{violationStats.disputedViolations}</Typography>
        </Paper>
        {violationStats.hasOverdueFines && (
          <Paper sx={{ p: 2, minWidth: 200, flex: 1, borderLeft: '4px solid', borderColor: 'error.main', bgcolor: 'error.light' }}>
            <Typography variant="body2" color="error.main">Overdue Fines</Typography>
            <Typography variant="h4" color="error.main">⚠️</Typography>
            <Typography variant="body2" color="error.main">Action Required</Typography>
          </Paper>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Violations ({violationsList.length})</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setRecordDialogOpen(true)}
        >
          Record Violation
        </Button>
      </Box>

      {/* Violations Table */}
      {violationsList.length === 0 ? (
        <Alert severity="info">No violations recorded for this provider.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Service Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Fine Amount</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {violationsList.map((violation: any, index: number) => {
                const effectiveFine = getEffectiveFineAmount(violation);
                const customer = violation.customerId;
                const serviceRequest = violation.serviceRequestId;

                return (
                  <TableRow key={violation._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{violation.violationNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(violation.violationDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Scheduled: {formatDate(violation.scheduledDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {customer ? (
                        <>
                          <Typography variant="body2">
                            {customer.firstName} {customer.lastName}
                          </Typography>
                          {customer.email && (
                            <Typography variant="caption" color="text.secondary">
                              {customer.email}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {serviceRequest?.address ? (
                        <Typography variant="body2">
                          {serviceRequest.address.street || 'N/A'}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(violation.status)}
                        color={getStatusColor(violation.status) as any}
                        size="small"
                      />
                      {violation.status === 'disputed' && violation.disputeReason && (
                        <Tooltip title={violation.disputeReason}>
                          <IconButton size="small" sx={{ ml: 1 }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {violation.appealDecision && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Appeal: {violation.appealDecision}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {effectiveFine > 0 ? (
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          ${effectiveFine.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Warning
                        </Typography>
                      )}
                      {violation.adjustedFineAmount !== undefined &&
                        violation.adjustedFineAmount !== violation.fineAmount && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Original: ${violation.fineAmount.toFixed(2)}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      {violation.adminNotes ? (
                        <Tooltip title={violation.adminNotes}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {violation.adminNotes}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                      {violation.disputeReason && (
                        <Typography variant="caption" color="secondary.main" display="block">
                          Dispute: {violation.disputeReason.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditViolation(violation)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Record Violation Dialog */}
      <Dialog open={recordDialogOpen} onClose={() => setRecordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Record Violation</Typography>
            <IconButton onClick={() => setRecordDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Service Request ID"
              value={recordForm.serviceRequestId}
              onChange={(e) => setRecordForm({ ...recordForm, serviceRequestId: e.target.value })}
              required
              fullWidth
              helperText="Enter the service request ID for the missed service"
            />
            <TextField
              label="Violation Date"
              type="date"
              value={recordForm.violationDate}
              onChange={(e) => setRecordForm({ ...recordForm, violationDate: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Admin Notes"
              value={recordForm.adminNotes}
              onChange={(e) => setRecordForm({ ...recordForm, adminNotes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              helperText="Internal notes (not visible to provider)"
            />
            <TextField
              label="Notes"
              value={recordForm.notes}
              onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
              helperText="General notes about the violation"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRecordViolation}
            variant="contained"
            disabled={loading || !recordForm.serviceRequestId}
          >
            {loading ? 'Recording...' : 'Record Violation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Violation Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Edit Violation #{selectedViolation?.violationNumber}
            </Typography>
            <IconButton onClick={() => setEditDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          {selectedViolation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="fine_pending">Fine Pending</MenuItem>
                  <MenuItem value="fine_unpaid">Fine Unpaid</MenuItem>
                  <MenuItem value="fine_collected">Fine Collected</MenuItem>
                  <MenuItem value="disputed">Disputed</MenuItem>
                  <MenuItem value="waived">Waived</MenuItem>
                  <MenuItem value="reduced">Reduced</MenuItem>
                </Select>
              </FormControl>

              {editForm.status === 'reduced' && (
                <TextField
                  label="Adjusted Fine Amount"
                  type="number"
                  value={editForm.adjustedFineAmount}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      adjustedFineAmount: parseFloat(e.target.value) || 0,
                    })
                  }
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={`Original fine: $${selectedViolation.fineAmount.toFixed(2)}`}
                />
              )}

              {selectedViolation.status === 'disputed' && (
                <>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Dispute Reason:</Typography>
                    <Typography variant="body2">{selectedViolation.disputeReason}</Typography>
                  </Alert>
                  <FormControl fullWidth>
                    <InputLabel>Appeal Decision</InputLabel>
                    <Select
                      value={editForm.appealDecision}
                      onChange={(e) => setEditForm({ ...editForm, appealDecision: e.target.value })}
                      label="Appeal Decision"
                    >
                      <MenuItem value="">No Decision</MenuItem>
                      <MenuItem value="approved">Approved (Waive Fine)</MenuItem>
                      <MenuItem value="partial">Partially Approved (Reduce Fine)</MenuItem>
                      <MenuItem value="denied">Denied (Uphold Fine)</MenuItem>
                    </Select>
                  </FormControl>
                  {editForm.appealDecision && (
                    <TextField
                      label="Appeal Decision Notes"
                      value={editForm.appealDecisionNotes}
                      onChange={(e) =>
                        setEditForm({ ...editForm, appealDecisionNotes: e.target.value })
                      }
                      multiline
                      rows={3}
                      fullWidth
                      helperText="Explain your decision on this appeal"
                    />
                  )}
                </>
              )}

              <TextField
                label="Admin Notes"
                value={editForm.adminNotes}
                onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })}
                multiline
                rows={3}
                fullWidth
                helperText="Internal notes (not visible to provider)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateViolation}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Violation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViolationsTab;

